/**
 * The depth counter's record of a run: a thinned depth-vs-time curve and the
 * scalars that summarise it.
 *
 * From Android DB v19 a survey run with an XTDC depth counter uploads this
 * alongside the survey, as evidence that the tool was physically run down the
 * hole and back rather than fabricated at the surface. The wire format and the
 * reasoning behind it are in docs/android-depth-trace.md; this module is its
 * decoder and the shot-on-curve cross-check.
 *
 * Two rules shape everything here.
 *
 * **Absence is the normal case.** Most surveys on every tool we ship have no
 * counter, so every field is missing and every function has to return a neutral
 * value rather than throw. The gate is `hasDepthRun` — `DepthSampleCount > 0` —
 * and never the tool model: any tool can be run with a counter and any tool can
 * be run without one, so keying off DeviceName would be wrong in both directions.
 *
 * **The scalars are authoritative and the trace is not.** Every DepthXxx figure
 * is accumulated from the raw 4 Hz stream, while the trace is thinned to keep a
 * survey's JSON small. Recomputing a scalar from the trace gives a smaller,
 * wrong number. Measured on DC Summary - 20260907 153421, the one real
 * counter survey: distance 31.2 m stored against 29.80 m recomputed, top speed
 * 42.0 against 35.3 m/min, range -0.1..20.2 against 0.0..20.0. So
 * `depthRunSummary` reads them verbatim, and the single derived value in this
 * module is the link-quality ratio.
 *
 * The one judgement made here is `depthRunVerdict`, the customer-facing
 * pass/fail on whether the shots agree with the counter. Every other figure is
 * reported and left alone — in particular the top speed, which has no configured
 * limit to be measured against.
 */

/**
 * How long a hole in the trace has to be before it stops being the heartbeat and
 * starts being a dropped link.
 *
 * A *stationary* tool still leaves a trail — the thinner emits a point every 30 s
 * with nothing else to say — so a hole longer than that means the counter stopped
 * reporting, which is the whole reason the heartbeat exists. Honest spacing is
 * therefore 30 s plus quantisation, and on the real trace the largest gap is
 * exactly 30.1 s (a TimeDelta of 301).
 *
 * The margin is measured rather than guessed. Over that trace's 57 points a
 * threshold of `> 30.0 s` splits it into 10 segments and `>= 30.0 s` into 29,
 * where 35 s, 45 s and 60 s all correctly give 3. 45 s sits in the middle of that
 * flat region and still catches a single missed heartbeat, which lands at 60 s.
 */
export const GAP_SECONDS = 45;

/** Metres per counter count when a trace does not say. Always 0.1 today. */
export const DEFAULT_COUNT_METRES = 0.1;

/**
 * Packets per second the counter reports at, for the link-quality ratio.
 *
 * 4, measured rather than assumed: the 2026-09-07 XTDC0004 survey received 3892
 * packets over 972.99 s of unbroken streaming, which is 4.000 Hz. This started
 * life as 5 on the handset's own mistaken assumption, which made a flawless link
 * read 80% here and — more seriously — scaled every speed the app reported by
 * 1.25. Both are fixed; if a future firmware changes the rate, this ratio
 * settling away from 1.0 across many surveys is the signal.
 */
export const SAMPLES_PER_SECOND = 4;

/**
 * True when this survey carries a depth-counter run.
 *
 * `DepthSampleCount` is the gate because it is the only field that cannot
 * legitimately be zero for a run that actually happened — `DepthCounterName` is
 * blank when the counter advertised no name, `DepthGapCount` is zero on a clean
 * run, and `DepthTrace` is a separate field that could in principle be missing.
 */
export function hasDepthRun(survey) {
    return Number(survey?.DepthSampleCount) > 0;
}

/**
 * Decodes the delta-encoded trace into points.
 *
 * `TimeDeltas` are deci-seconds since `Epoch`, `CountDeltas` are counter counts,
 * both running sums. `CountMetres` is read rather than assumed so a future
 * counter with finer resolution decodes without a change here.
 *
 * @returns {Array<{at: Date, depth: number, startsSegment: boolean}>}
 */
export function decodeDepthTrace(trace) {
    const times = trace?.TimeDeltas;
    const counts = trace?.CountDeltas;
    if (!Array.isArray(times) || times.length === 0 || !Array.isArray(counts)) return [];

    const breaks = new Set(trace.Breaks || []);
    const unit = Number.isFinite(trace.CountMetres) ? trace.CountMetres : DEFAULT_COUNT_METRES;
    const epoch = Number(trace.Epoch) || 0;

    const points = [];
    let t = 0;
    let c = 0;
    for (let i = 0; i < times.length; i++) {
        t += Number(times[i]) || 0;
        c += Number(counts[i]) || 0;
        points.push({
            at: new Date(epoch + t * 100),
            depth: c * unit,
            startsSegment: i === 0 || breaks.has(i)
        });
    }
    return points;
}

/**
 * The trace split into the stretches that can be read as continuous movement.
 *
 * Two different things break continuity and they do not mean the same thing.
 *
 * A **break** is bookkeeping: the operator zeroed the counter, typed a new datum,
 * inverted the direction, or reconnected. The depth step across it is not
 * movement, which is why `DepthDistanceMetres` already excludes those steps and
 * why a speed must never be computed across one. On the real survey the operator
 * zeroed twice before the counter came up reading 10 m, so a single polyline
 * would draw that 10 m of bookkeeping as a vertical cliff.
 *
 * A **gap** is a dead link: nothing was recorded, so nothing is known. The spec
 * delimits segments by breaks alone, but taken literally that would interpolate a
 * shot straight across a five-minute dropout — which contradicts its own
 * instruction to render gaps as gaps. Both split the run; `startedBy` is what
 * keeps them distinguishable downstream.
 *
 * @returns {Array<{points: Array, startedBy: 'start'|'break'|'gap', from: Date, to: Date, spanSeconds: number}>}
 */
export function traceSegments(trace) {
    const points = decodeDepthTrace(trace);
    if (points.length === 0) return [];

    const segments = [];
    let current = null;
    for (let i = 0; i < points.length; i++) {
        const point = points[i];
        let startedBy = null;
        if (i === 0) {
            startedBy = 'start';
        } else if (point.startsSegment) {
            // A break and a gap at the same index report as a break: the
            // bookkeeping fact is the more specific of the two.
            startedBy = 'break';
        } else if ((point.at - points[i - 1].at) / 1000 > GAP_SECONDS) {
            startedBy = 'gap';
        }

        if (startedBy) {
            current = { points: [], startedBy };
            segments.push(current);
        }
        current.points.push(point);
    }

    return segments.map(segment => {
        const from = segment.points[0].at;
        const to = segment.points[segment.points.length - 1].at;
        return { ...segment, from, to, spanSeconds: (to - from) / 1000 };
    });
}

/**
 * The run's summary figures, as recorded.
 *
 * Every scalar is passed through untouched — see the header on why recomputing
 * one from the trace is wrong. `linkQuality` is the only derived value: the
 * fraction of expected packets that actually arrived, which nothing has ever
 * measured before (1.000 on the one real survey). It is null rather than Infinity
 * when there is no connected time to divide by.
 *
 * @returns {object|null} null when this survey has no depth run.
 */
export function depthRunSummary(survey) {
    if (!hasDepthRun(survey)) return null;

    const segments = traceSegments(survey.DepthTrace);
    const connectedSeconds = num(survey.DepthConnectedSeconds);
    const sampleCount = num(survey.DepthSampleCount);
    const expected = connectedSeconds === null ? null : connectedSeconds * SAMPLES_PER_SECOND;

    return {
        counterName: survey.DepthCounterName || '',
        sampleCount,
        connectedSeconds,
        distanceMetres: num(survey.DepthDistanceMetres),
        maxSpeedMpm: num(survey.DepthMaxSpeedMpm),
        minMetres: num(survey.DepthMinMetres),
        maxMetres: num(survey.DepthMaxMetres),
        gapCount: num(survey.DepthGapCount),
        gapSeconds: num(survey.DepthGapSeconds),
        linkQuality: expected && sampleCount !== null ? sampleCount / expected : null,
        pointCount: segments.reduce((total, s) => total + s.points.length, 0),
        segmentCount: segments.length,
        breakCount: segments.filter(s => s.startedBy === 'break').length,
        traceGapCount: segments.filter(s => s.startedBy === 'gap').length
    };
}

/**
 * The shot-on-curve cross-check: does each shot's depth lie on the trace at that
 * shot's own timestamp?
 *
 * This is the part that resists fabrication. The shot list and the trace are two
 * independent records of the same run, so a fabricated survey has to satisfy both
 * at once — a much harder thing to fake than either alone.
 *
 * A mark that falls in no segment is **uncovered, never failed**. Shots taken
 * before the counter was connected have no trace to lie on, and that is normal.
 * The `reason` says which kind of nothing it is, because "before the counter was
 * connected" and "inside a link dropout" mean different things to a reviewer.
 *
 * This function reports and does not judge; `depthRunVerdict` is what applies the
 * threshold. Residuals on the one real survey run 0.000-0.156 m (mean 0.028).
 *
 * **No AQ timestamp correction is applied, deliberately.** The spec warns that
 * the app stamps an AQ shot at now + 15 s to aim at the middle of its stillness
 * window, and invites a correction if the residuals show a systematic offset.
 * They do not: on the real survey every SavedShots[i].Timestamp is byte-identical
 * to its DepthTimeRecords[i].TimeStamp, so the offset is carried by the depth
 * mark too and cancels. Subtracting 15 s makes the fit three and a half times
 * worse — mean 0.028 -> 0.099 m, worst 0.156 -> 0.605 m.
 *
 * Marks come from SavedShots, falling back to DepthTimeRecords for a run with
 * depth marks and no shots, which is what a GammaTrak logger produces.
 */
export function shotCoverage(survey) {
    const segments = traceSegments(survey?.DepthTrace);
    const shots = Array.isArray(survey?.SavedShots) ? survey.SavedShots : [];
    const source = shots.length > 0 ? 'shots' : 'depthMarks';
    const rows = shots.length > 0
        ? shots
        : (Array.isArray(survey?.DepthTimeRecords) ? survey.DepthTimeRecords : []);

    const marks = rows
        // Android tools serialise a shot's time as Timestamp and the Navigator
        // desktop tools as TimeStamp; every record stream uses TimeStamp.
        // The row itself rides along so a table rendering shots in its own order
        // can find each one's result again — see coverageByShot.
        .map(row => ({ shot: row, at: new Date(row.Timestamp ?? row.TimeStamp), depth: Number(row.Depth) }))
        .filter(mark => !isNaN(mark.at.getTime()) && Number.isFinite(mark.depth))
        // Never trust array order: SavedShots and DepthTimeRecords are both
        // stored newest-first, because the app prepends each new one.
        .sort((a, b) => a.at - b.at)
        .map(mark => coverMark(mark, segments));

    const covered = marks.filter(mark => mark.status === 'covered');
    const residuals = covered.map(mark => mark.residual);

    const uncoveredByReason = {};
    marks.filter(mark => mark.status === 'uncovered').forEach(mark => {
        uncoveredByReason[mark.reason] = (uncoveredByReason[mark.reason] || 0) + 1;
    });

    return {
        source,
        marks,
        total: marks.length,
        covered: covered.length,
        coveredFraction: marks.length > 0 ? covered.length / marks.length : null,
        meanResidual: residuals.length > 0
            ? residuals.reduce((sum, r) => sum + r, 0) / residuals.length
            : null,
        maxResidual: residuals.length > 0 ? Math.max(...residuals) : null,
        uncoveredByReason
    };
}

/**
 * The cross-check result for each shot, keyed by the shot itself, for a table
 * that renders rows in its own order and needs each row's answer back.
 *
 * Keyed by the shot object rather than by `Id`, the same choice
 * `positionsByShot` makes and for the same reason: HDD shots carry no Id at all,
 * so an Id-keyed map collapses to a single entry. Callers filter and sort
 * `SavedShots`, which preserves the object identities this map uses.
 *
 * Empty for a run whose marks came from `DepthTimeRecords` rather than shots —
 * there is no shot table to fill in that case.
 */
export function coverageByShot(survey) {
    const map = new Map();
    const coverage = shotCoverage(survey);
    if (coverage.source !== 'shots') return map;
    coverage.marks.forEach(mark => map.set(mark.shot, mark));
    return map;
}

/**
 * How far a shot may sit from the counter before the run is called into
 * question, in metres.
 *
 * Asked for by the customer, who wants one light rather than a table of
 * residuals. A metre is comfortably above what an honest survey produces: the
 * trace's own thinning bounds the error well under half a metre — a point is
 * kept once movement reaches 1.0 m, and any excursion of 0.5 m out and back is
 * kept as a turning point — and the real run's worst shot sits at 0.156 m.
 *
 * It is therefore a threshold on gross disagreement, not a precision standard.
 * A shot a metre off its own depth counter is not a shot that drifted; it is a
 * shot that was not taken where the survey says it was.
 */
export const MAX_SHOT_RESIDUAL_METRES = 1.0;

/**
 * The customer-facing verdict: did the shots agree with the depth counter?
 *
 * Three outcomes, not two. `pass` and `fail` are the light the customer asked
 * for; `unchecked` is what has to exist alongside them, because a run where no
 * shot could be compared has produced no evidence either way and showing it
 * green would claim some. That happens for real — a counter connected part-way
 * through a run leaves the earlier shots with no trace to lie on.
 *
 * Only covered marks are judged. An uncovered one has no residual to exceed
 * anything, and treating absence as failure would fail every survey where the
 * operator connected the counter late.
 *
 * @returns {object|null} null when this survey has no depth run.
 */
export function depthRunVerdict(survey) {
    if (!hasDepthRun(survey)) return null;

    const coverage = shotCoverage(survey);
    const covered = coverage.marks.filter(mark => mark.status === 'covered');
    const failing = covered.filter(mark => mark.residual > MAX_SHOT_RESIDUAL_METRES);

    return {
        status: covered.length === 0 ? 'unchecked' : (failing.length > 0 ? 'fail' : 'pass'),
        threshold: MAX_SHOT_RESIDUAL_METRES,
        checked: covered.length,
        total: coverage.total,
        failing: failing.length,
        worstResidual: coverage.maxResidual,
        source: coverage.source
    };
}

// Where one mark sits relative to the run.
function coverMark(mark, segments) {
    const uncovered = (reason) => ({ ...mark, status: 'uncovered', reason, interpolated: null, residual: null });
    if (segments.length === 0) return uncovered('no-trace');

    const ms = mark.at.getTime();
    for (const segment of segments) {
        if (ms < segment.from.getTime() || ms > segment.to.getTime()) continue;
        const interpolated = interpolateAt(segment, ms);
        // A segment of one point spans no time, so landing inside it says
        // nothing; interpolateAt refuses and the mark falls through to the
        // out-of-segment reasons below.
        if (interpolated === null) break;
        return {
            ...mark,
            status: 'covered',
            reason: null,
            interpolated,
            residual: Math.abs(mark.depth - interpolated)
        };
    }

    const first = segments[0];
    const last = segments[segments.length - 1];
    if (ms < first.from.getTime()) return uncovered('before-trace');
    if (ms > last.to.getTime()) return uncovered('after-trace');

    // Between two segments. Which kind of hole it fell into is the useful part:
    // a break is the operator re-datuming, a gap is the link having dropped.
    for (let i = 1; i < segments.length; i++) {
        if (ms > segments[i - 1].to.getTime() && ms < segments[i].from.getTime()) {
            return uncovered(segments[i].startedBy === 'gap' ? 'in-gap' : 'across-break');
        }
    }
    return uncovered('across-break');
}

// Linear interpolation within one segment. Never across a segment boundary: the
// depth step across a break is bookkeeping rather than movement, and nothing at
// all was recorded across a gap.
function interpolateAt(segment, ms) {
    const points = segment.points;
    if (points.length < 2) return null;
    if (ms < points[0].at.getTime() || ms > points[points.length - 1].at.getTime()) return null;

    for (let i = 1; i < points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        const from = a.at.getTime();
        const to = b.at.getTime();
        if (ms < from || ms > to) continue;
        // Two points can legitimately share a timestamp - a TimeDelta of 0 is
        // valid, and the spec's own pinned sample opens with one.
        if (to === from) return b.depth;
        return a.depth + ((ms - from) / (to - from)) * (b.depth - a.depth);
    }
    return null;
}

// A stored scalar, or null when it is absent or unreadable. Not `?? 0`: a
// missing figure and a measured zero mean different things here, and a run with
// no gaps genuinely reports DepthGapCount 0.
function num(value) {
    const parsed = Number(value);
    return value === null || value === undefined || !Number.isFinite(parsed) ? null : parsed;
}
